/**
 * Y-axis gutter sizing for the Recharts-backed chart primitives.
 *
 * recharts is pinned at 2.2.0, whose `<YAxis>` has no `width="auto"` (that
 * arrived in 2.13). The gutter width must therefore be a computed number — a
 * fixed width clips any tick label wider than it (e.g. "$150.00" rendered into
 * a 48px gutter shows only "50.00"). `computeYAxisWidth` derives a width from
 * the longest formatted tick so labels are never clipped, shared by every
 * cartesian chart primitive so the rule lives in one place.
 */

/** Floor so short labels (e.g. "0", "5") still get breathing room. */
export const CHART_YAXIS_MIN_WIDTH = 40;

/** Ceiling so a runaway label can never eat the plot area. */
export const CHART_YAXIS_MAX_WIDTH = 96;

// Per-character estimate calibrated to the 12px Inter tick font, plus padding.
// A pure estimate (no DOM measurement) keeps the helper jsdom/SSR-safe and
// unit-testable; the padding also absorbs recharts rounding the top tick up to
// a "nice" number with one more digit than the raw data maximum.
const PX_PER_CHAR = 7.5;
const AXIS_PADDING = 14;

/**
 * Computes a Y-axis gutter width (px) wide enough for the longest formatted tick
 * label across `values`, clamped to [CHART_YAXIS_MIN_WIDTH, CHART_YAXIS_MAX_WIDTH].
 *
 * Pass the same `formatter` wired to the chart's `yAxisFormatter` so the estimate
 * matches what renders. For a stacked bar chart, pass the per-x stack sums (the
 * top tick reflects the tallest stack, not a single series).
 */
export function computeYAxisWidth(values: number[], formatter?: (value: number) => string): number {
	if (!values.length) return CHART_YAXIS_MIN_WIDTH;
	const longest = values.reduce((max, value) => {
		const label = formatter ? formatter(value) : String(value);
		return Math.max(max, label.length);
	}, 0);
	const width = Math.ceil(longest * PX_PER_CHAR) + AXIS_PADDING;
	return Math.min(CHART_YAXIS_MAX_WIDTH, Math.max(CHART_YAXIS_MIN_WIDTH, width));
}
